import {
  field,
  formatEther,
  parseEther,
  readContract,
  writeContractAndWait,
} from "../viem";
import config from "../../config/config";

import XPMarketAbi from "../../abis/XPMarket.json";
import { XPMarketItemType } from "../../types/XPMarketItem";

const address = config.xpMarketAddress;
const abi = XPMarketAbi;

export const listXP = async (
  xpType: number,
  listingType: number,
  listingToken: string,
  startPrice: string,
  duration: number,
  account: string
) => {
  const startPriceWei = parseEther(startPrice);

  const receipt = await writeContractAndWait({
    address,
    abi,
    functionName: "listXp",
    args: [xpType, listingType, listingToken, startPriceWei, duration],
    account,
  });

  return receipt;
};

export const getSwapTokens = async () => {
  const swapTokens: any = await readContract({
    address,
    abi,
    functionName: "getSwapTokens",
  });
  const list = field(swapTokens, "list", 0) || [];
  const prices = field(swapTokens, "prices", 1) || [];
  const swapTokensArray: {
    address: string;
    price: number;
  }[] = list.map((token: any, index: number) => ({
    address: field(token, "tokenAddress", 2),
    price: Number(formatEther(prices[index])),
  }));
  return swapTokensArray;
};

/** A bid must be strictly more than 105% of the current price. */
const NEXT_BID_NUMERATOR = 105n;
const NEXT_BID_DENOMINATOR = 100n;

const toIntegerWeiString = (currentPriceWei: string) => {
  if (!/^\d+$/.test(currentPriceWei)) {
    throw new Error("XP market price must be an integer wei value");
  }
  return currentPriceWei;
};

/**
 * Exact minimum valid next bid, in wei.
 * Must use BigInt integer math — JS Number / `* 1.05` drops low wei digits
 * and the resulting bid reverts InvalidPrice.
 */
export const getNextBidPriceWei = (currentPriceWei: string) => {
  const current = BigInt(toIntegerWeiString(currentPriceWei));
  return (
    (current * NEXT_BID_NUMERATOR) / NEXT_BID_DENOMINATOR + 1n
  ).toString();
};

/** Rounds a wei amount up to the next whole rounding unit. */
export const roundUpWei = (amountWei: string, roundingUnitWei: string) => {
  const amount = BigInt(toIntegerWeiString(amountWei));
  const unit = BigInt(toIntegerWeiString(roundingUnitWei));

  if (unit <= 0n) {
    throw new Error("XP market rounding unit must be greater than zero");
  }

  return (((amount + unit - 1n) / unit) * unit).toString();
};

export const bidOnAuctionItemXPMarket = async (
  itemId: number,
  swapTokenId: number,
  nextBidPriceWei: string,
  account: string
) => {
  if (swapTokenId === 0) {
    const receipt = await writeContractAndWait({
      address,
      abi,
      functionName: "bidOnAuctionItem",
      args: [itemId, swapTokenId, nextBidPriceWei],
      account,
      value: nextBidPriceWei,
    });
    return receipt;
  } else {
    const receipt = await writeContractAndWait({
      address,
      abi,
      functionName: "bidOnAuctionItem",
      args: [itemId, swapTokenId, nextBidPriceWei],
      account,
    });
    return receipt;
  }
};

export const finishAuctionItem = async (itemId: number, account: string) => {
  const receipt = await writeContractAndWait({
    address,
    abi,
    functionName: "finishAuctionItem",
    args: [itemId],
    account,
  });

  return receipt;
};

export const cancelListingXPMarket = async (
  itemId: number,
  account: string
) => {
  const receipt = await writeContractAndWait({
    address,
    abi,
    functionName: "cancelListing",
    args: [itemId],
    account,
  });

  return receipt;
};

export const getXPMarketItem = async (itemId: number) => {
  const item: any = await readContract({
    address,
    abi,
    functionName: "getMarketItem",
    args: [itemId],
  });
  const res: XPMarketItemType = {
    id: Number(itemId),
    seller: item.owner,
    buyer: item.buyer,
    xpType: Number(item.xpType),
    xpPoint: Number(item.xpPoint),
    currentPrice: item.currentPrice.toString(),
    startingPrice: item.startPrice.toString(),
    listingType: Number(item.listingType),
    expireAt: Number(item.endTimestamp),
    timestamp: Number(item.endTimestamp),
    token: item.listingToken,
    status: Number(item.status),
    txHash: "",
  };
  return res;
};
